import React, { useState, useEffect } from "react";
import {
  PlusIcon,
  SparklesIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import SubPageLayout from "../../components/shared/SubPageLayout";
import NewsFeed from "../../components/market-news/NewsFeed";
import NewsCard from "../../components/market-news/NewsCard";
import MarketSidebar from "../../components/market-news/MarketSidebar";
import EconomicCalendar from "../../components/market-news/EconomicCalendar";
import AIInsightModal from "../../components/market-news/AIInsightModal";
import Modal from "../../components/shared/Modal";
import api from '../../utils/apiConfig';

import logger from '../../utils/logger';

// Add loading skeleton component at the top of the file
const NewsCardSkeleton = () => (
  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6 mb-4 animate-pulse">
    <div className="flex gap-2 mb-2">
      <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
      <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
    <div className="h-7 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
    <div className="space-y-2">
      <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
      <div className="h-4 w-5/6 bg-gray-200 dark:bg-gray-700 rounded"></div>
      <div className="h-4 w-4/6 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
    <div className="flex justify-between items-center mt-4">
      <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
  </div>
);

const MarketNews = () => {
  // Live market data state
  const [marketIndices, setMarketIndices] = useState([]);
  const [gainers, setGainers] = useState([]);
  const [losers, setLosers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [indicesLoading, setIndicesLoading] = useState(true);
  const [indicesError, setIndicesError] = useState(null);

  // News state (hybrid cache)
  const newsTabs = [
    "Stocks",
    "ETFs",
    "Futures",
    "Commodities",
    "Cryptocurrency",
    "Banking/Finance",
    "Real Estate",
    "Economy/Macro",
    "Technology",
    "General Business",
  ];
  const [activeTab, setActiveTab] = useState("Stocks");
  const [selectedSources, setSelectedSources] = useState([]);
  const [newsCache, setNewsCache] = useState({}); // { [tab+sources]: { news, lastUpdated, loading, error } }
  const [refreshing, setRefreshing] = useState(false);

  // Modal and tagging state
  const [showModal, setShowModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedArticleForSummary, setSelectedArticleForSummary] =
    useState(null);

  // Model tags available for selection
  const availableTags = [
    "RN15",
    "RN30",
    "RN45",
    "RN60",
    "Equity",
    "Sector",
    "Artificial Intelligence & Innovation",
    "Non-Qualified Equity",
    "Non-Qualified Bond",
  ];

  // Available news sources (for filter UI)
  const newsSources = [
    "Yahoo Finance",
    "CNBC",
    "CNN",
    "Bloomberg",
    "Morningstar",
    "Wall Street Journal",
    "Financial Times",
    "MarketWatch",
    "Reuters",
    "ZeroHedge",
    "MotleyFool",
    "gurufocus.com",
  ];
  // Map for display names
  const newsSourceDisplayNames = {
    "gurufocus.com": "GuruFocus",
  };

  // Pagination state
  const ARTICLES_PER_PAGE = 5;
  const [page, setPage] = useState(1);

  // Modal for unavailable news source
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);

  // Helper to build cache key
  const getCacheKey = (tab, sources) => `${tab}::${sources.sort().join(",")}`;

  // Fetch news for the current tab and sources
  const fetchNews = async (tab, sources, { forceRefresh = false } = {}) => {
    const cacheKey = getCacheKey(tab, sources);
    if (!forceRefresh && newsCache[cacheKey] && !newsCache[cacheKey].error) {
      // Already have cached news, don't fetch unless forced
      return;
    }
    setNewsCache((prev) => ({
      ...prev,
      [cacheKey]: { ...(prev[cacheKey] || {}), loading: true, error: null },
    }));
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.append("primaryTab", tab);
      if (sources.length > 0) params.append("source", sources.join(","));
      params.append("limit", 20);
      const { data } = await api.get(`/market/news`, { params });
      setNewsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          news: data.news || [],
          lastUpdated: new Date(),
          loading: false,
          error: null,
        },
      }));
    } catch (err) {
      setNewsCache((prev) => ({
        ...prev,
        [cacheKey]: {
          news: [],
          lastUpdated: prev[cacheKey]?.lastUpdated || null,
          loading: false,
          error: err.message || "Failed to fetch news",
        },
      }));
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch news on tab or source change
  useEffect(() => {
    const cacheKey = getCacheKey(activeTab, selectedSources);
    if (!newsCache[cacheKey] || newsCache[cacheKey].error) {
      fetchNews(activeTab, selectedSources);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedSources]);

  // Manual refresh handler
  const handleRefresh = () => {
    fetchNews(activeTab, selectedSources, { forceRefresh: true });
  };

  // Filtered news for current tab/sources
  const cacheKey = getCacheKey(activeTab, selectedSources);
  let { news = [], lastUpdated, loading, error } = newsCache[cacheKey] || {};

  // Pagination logic
  const totalPages = Math.ceil(news.length / ARTICLES_PER_PAGE);
  const paginatedNews = news.slice(
    (page - 1) * ARTICLES_PER_PAGE,
    page * ARTICLES_PER_PAGE,
  );

  // Reset page to 1 when tab or sources change
  useEffect(() => {
    setPage(1);
  }, [activeTab, selectedSources]);

  // If filtering by a source not in the list, show all articles for the tab
  const validSources = new Set(newsSources.map((s) => s.toLowerCase()));
  const hasValidSource = selectedSources.every((s) =>
    validSources.has(s.toLowerCase()),
  );
  const displayNews = hasValidSource
    ? paginatedNews
    : news.slice(0, ARTICLES_PER_PAGE);

  useEffect(() => {
    setIndicesLoading(true);
    api.get("/market/market-data")
      .then((res) => {
        const data = res.data;
        // Indices
        const indexDisplayNames = {
          "S&P 500": "S&P 500",
          "DOW JONES": "Dow Jones Industrial Average",
          NASDAQ: "NASDAQ",
          "Russell 2000": "Russell 2000",
          VIX: "VIX^",
          AGG: "Aggregate Bond Index (AGG)",
        };
        const indices = [
          "S&P 500",
          "DOW JONES",
          "NASDAQ",
          "Russell 2000",
          "VIX",
          "AGG",
        ].map((name) => {
          const idx = data.indices[name];
          return {
            name: indexDisplayNames[name] || name,
            value:
              idx?.price?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              }) ?? "N/A",
            change:
              idx?.change !== undefined && idx?.change !== null
                ? (idx.change >= 0 ? "+" : "") + idx.change.toFixed(2)
                : "",
            percentChange:
              idx?.percent_change !== undefined && idx?.percent_change !== null
                ? (idx.percent_change >= 0 ? "+" : "") +
                  idx.percent_change.toFixed(2) +
                  "%"
                : "",
            color: idx?.color || "gray",
          };
        });
        setMarketIndices(indices);
        // Commodities
        const comms = Object.entries(data.commodities).map(([name, c]) => ({
          name,
          value:
            c?.price !== undefined && c?.price !== null
              ? `$${c.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
              : "N/A",
          change:
            c?.change !== undefined && c?.change !== null
              ? (c.change >= 0 ? "+" : "") + c.change.toFixed(2)
              : "",
          percentChange:
            c?.percent_change !== undefined && c?.percent_change !== null
              ? (c.percent_change >= 0 ? "+" : "") +
                c.percent_change.toFixed(2) +
                "%"
              : "",
          color: c?.color || "gray",
        }));
        setCommodities(comms);
        // Gainers
        setGainers(
          (data.gainers || []).map((g) => ({
            symbol: g.symbol,
            change:
              g.change !== undefined && g.change !== null
                ? (g.change >= 0 ? "+" : "") + g.change.toFixed(2)
                : "",
            percentChange:
              g.percent_change !== undefined && g.percent_change !== null
                ? (g.percent_change >= 0 ? "+" : "") +
                  g.percent_change.toFixed(2) +
                  "%"
                : "",
            color: g.color || "gray",
          })),
        );
        // Losers
        setLosers(
          (data.losers || []).map((l) => ({
            symbol: l.symbol,
            change:
              l.change !== undefined && l.change !== null
                ? (l.change >= 0 ? "+" : "") + l.change.toFixed(2)
                : "",
            percentChange:
              l.percent_change !== undefined && l.percent_change !== null
                ? (l.percent_change >= 0 ? "+" : "") +
                  l.percent_change.toFixed(2) +
                  "%"
                : "",
            color: l.color || "gray",
          })),
        );
        setIndicesLoading(false);
      })
      .catch((err) => {
        let errorMsg = "Failed to load market data";
        if (err && err.message) {
          errorMsg += `: ${err.message}`;
        }
        setIndicesError(errorMsg);
        setIndicesLoading(false);
        logger.error("Market data fetch error:", err);
      });
  }, []);

  const handleTagClick = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleSourceClick = (source) => {
    // Check if this source has any news in the current tab
    const cacheKey = getCacheKey(activeTab, [source]);
    const hasNews = (newsCache[cacheKey]?.news || []).length > 0;
    if (!hasNews) {
      setShowUnavailableModal(true);
      return;
    }
    setSelectedSources((prev) =>
      prev.includes(source)
        ? prev.filter((s) => s !== source)
        : [...prev, source],
    );
  };

  const handleAddToRD = (news) => {
    setSelectedNews(news);
    setShowModal(true);
    setSelectedTags([]);
  };

  const handleContributeResearch = () => {
    // TODO: Implement the API call to save the research
    logger.info("Contributing research:", {
      news: selectedNews,
      tags: selectedTags,
    });
    setShowModal(false);
    setSelectedTags([]);
  };

  // Mock data for economic calendar
  const economicEvents = [
    {
      time: "8:30 AM",
      event: "Non-Farm Payrolls",
      actual: "216K",
      forecast: "175K",
      previous: "199K",
      impact: "High",
    },
    {
      time: "10:00 AM",
      event: "ISM Services PMI",
      actual: "50.6",
      forecast: "52.0",
      previous: "51.8",
      impact: "Medium",
    },
    {
      time: "2:00 PM",
      event: "FOMC Minutes",
      actual: "-",
      forecast: "-",
      previous: "-",
      impact: "High",
    },
  ];

  // Mock AI summary function - in real implementation, this would call an AI service
  const getAISummary = (article) => {
    return {
      keyPoints: [
        "Main impact: " + article.summary.split("...")[0],
        "Potential market implications: Moderate to significant",
        "Sectors affected: Technology, Finance",
        "Timeline: Short to medium term",
      ],
      sentiment: "Neutral to Positive",
      marketImpact:
        "The news suggests potential market movements in related sectors, with implications for portfolio rebalancing consideration.",
    };
  };

  const handleShowAISummary = (article) => {
    setSelectedArticleForSummary(article);
    setShowSummaryModal(true);
  };

  const breadcrumbItems = [
    { label: "Portfolio Management", link: "/portfolio-management" },
    { label: "Market News", link: "/portfolio-management/market-news" },
  ];

  return (
    <SubPageLayout
      title="Market News"
      subtitle="Stay updated with the latest market news and analysis"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="flex items-center justify-between mb-4">
        <div></div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-500"
            disabled={loading || refreshing}
            title="Refresh News"
          >
            <ArrowPathIcon
              className={`h-5 w-5 ${loading || refreshing ? "animate-spin" : ""}`}
            />
          </button>
          {lastUpdated && (
            <span className="text-xs text-gray-500 dark:text-gray-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6">
        {/* Main News Feed */}
        <div className="col-span-8">
          {loading || refreshing ? (
            <div>
              <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                <nav className="flex space-x-8">
                  {newsTabs.map((tab) => (
                    <div
                      key={tab}
                      className="py-2 px-1 text-sm font-medium border-b-2 border-transparent animate-pulse"
                    >
                      <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  ))}
                </nav>
              </div>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <NewsCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : (
            <NewsFeed
              news={displayNews}
              tabs={newsTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              sources={newsSources}
              selectedSources={selectedSources}
              onSourceClick={handleSourceClick}
              onShowAISummary={handleShowAISummary}
              onAddToResearch={handleAddToRD}
              newsSourceDisplayNames={newsSourceDisplayNames}
            />
          )}
          {/* Pagination Controls */}
          {!loading && !refreshing && news.length > ARTICLES_PER_PAGE && (
            <div className="flex justify-center mt-4 space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2 py-1 rounded bg-gray-100 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-500">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2 py-1 rounded bg-gray-100 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
        {/* Sidebar */}
        <div className="col-span-4">
          <MarketSidebar
            indices={indicesLoading ? [] : marketIndices}
            economicEvents={economicEvents}
            gainers={gainers}
            losers={losers}
            commodities={commodities}
          />
          {indicesError && (
            <div className="text-red-600 text-sm mt-2">{indicesError}</div>
          )}
          <EconomicCalendar events={economicEvents} />
        </div>
      </div>
      {/* AI Summary Modal */}
      <AIInsightModal
        article={selectedArticleForSummary}
        summary={
          selectedArticleForSummary
            ? getAISummary(selectedArticleForSummary)
            : null
        }
        open={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        onAddToResearch={handleAddToRD}
      />
      {/* R&D Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} maxWidth="2xl">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Add to Portfolio R&D for next rebalancing
          </h2>
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Selected Article:
            </h3>
            <p className="text-gray-900 dark:text-gray-100">{selectedNews?.title}</p>
          </div>
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              Select Models:
            </h3>
            <div className="flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                    ${
                      selectedTags.includes(tag)
                        ? "bg-green-100 text-green-800 border-2 border-green-600"
                        : "bg-gray-100 text-gray-900 dark:text-gray-100 border-2 border-transparent hover:border-gray-300"
                    }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleContributeResearch}
              disabled={selectedTags.length === 0}
              className={`px-4 py-2 rounded-md ${
                selectedTags.length === 0
                  ? "bg-gray-300 text-gray-500 dark:text-gray-500 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              Contribute Research
            </button>
          </div>
        </div>
      </Modal>
      {/* Unavailable News Source Modal */}
      <Modal isOpen={showUnavailableModal} onClose={() => setShowUnavailableModal(false)} maxWidth="md">
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
            News integration not active
          </h2>
          <p className="mb-4 text-gray-900 dark:text-gray-100">
            Set up or request firm level API in Administrative - Firm Settings.
          </p>
          <a
            href="/administrative/firm-settings#integration-settings"
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-700 hover:text-green-800 dark:text-green-500 dark:hover:text-green-400 underline mb-4 inline-block"
          >
            Go to Firm Settings
          </a>
          <div className="flex justify-end">
            <button
              onClick={() => setShowUnavailableModal(false)}
              className="px-4 py-2 rounded bg-gray-200 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </SubPageLayout>
  );
};

export default MarketNews;
